import { useMemo, useRef, useEffect, useCallback } from 'react';
import { Interval } from '../helpers/Interval';

type TimerBaseOptions = {
  downInterval?: number;
};

type TimerOptions = TimerBaseOptions &
  (
    | {
        onDown: (remainingTime: number) => void;
        onEnd?: () => void;
      }
    | {
        onDown?: (remainingTime: number) => void;
        onEnd: () => void;
      }
  );

function checkAssertions(downInterval: number, duration: number): boolean {
  return !(duration % downInterval !== 0 && downInterval > duration);
}

export const useTimer = (
  duration: number,
  options: TimerOptions
): {
  start: () => void;
  stopAndClear: () => void;
} => {
  const { downInterval = 1000, onDown, onEnd } = options;

  const interval = useMemo(() => new Interval(), []);
  const savedOnDown = useRef<(remainingTime: number) => void>();
  const savedOnEnd = useRef<() => void>();

  if (!checkAssertions(downInterval, duration)) {
    throw new Error('useTimer Input parameters are not valid');
  }

  useEffect(() => {
    savedOnDown.current = onDown;
    savedOnEnd.current = onEnd;
  }, [onDown, onEnd]);

  const remainingTime = useRef(duration);

  const tickFunction = useCallback(() => {
    remainingTime.current -= downInterval;
    savedOnDown.current?.(remainingTime.current);
    if (remainingTime.current === 0) {
      savedOnEnd.current?.();
      interval.stop();
    }
  }, [downInterval, interval]);

  const start = useCallback(() => {
    remainingTime.current = duration;
    interval.start(tickFunction, downInterval);
  }, [downInterval, duration, interval, tickFunction]);

  const stopAndClear = useCallback(() => {
    interval.stop();
  }, [interval]);

  return { start, stopAndClear };
};
